import { useCallback } from 'react';
import { trpc } from '@client-trpc';
import { useNotifications } from '@client/foundation/notifications/notifications.hook';
import type { TemplateType } from '@server/trading-templates/schemas/input.schema';
import type { TradingTemplate } from '@server/trading-templates/schemas/output.schema';
import { useWidget } from '@client/widgets/widget.hook';
import { templateValueToFormIndicators } from '../indicators-template-resolver';
import { templateValueToFormStrategy } from '../strategy-template-resolver';

const isDuplicateEnabledTemplateType = (
  templateType: TemplateType
): templateType is 'indicators' | 'strategy' | 'signalBlock' =>
  templateType === 'indicators' ||
  templateType === 'strategy' ||
  templateType === 'signalBlock';

export const useDuplicateTemplate = () => {
  const utils = trpc.useUtils();
  const trpcClient = utils.client;
  const { openWidget } = useWidget();
  const { notify } = useNotifications();

  const getTemplatesByType = useCallback(
    async (templateType: TemplateType): Promise<TradingTemplate[]> => {
      const cachedTemplates =
        utils.tradingTemplates.tradingTemplates.getData({ templateType }) ?? [];
      if (cachedTemplates.length > 0) {
        return cachedTemplates;
      }

      return trpcClient.tradingTemplates.tradingTemplates.query({
        templateType
      });
    },
    [trpcClient.tradingTemplates.tradingTemplates, utils]
  );

  const getTemplateById = useCallback(
    async (
      templateType: TemplateType,
      templateId: string
    ): Promise<TradingTemplate | undefined> => {
      const templates = await getTemplatesByType(templateType);
      return templates.find((template) => template.id === templateId);
    },
    [getTemplatesByType]
  );

  const onDuplicate = useCallback(
    (templateType: TemplateType) => (templateId: string, templateName: string) => {
      if (!isDuplicateEnabledTemplateType(templateType)) {
        return;
      }
      void (async () => {
        try {
          const sourceTemplate = await getTemplateById(templateType, templateId);
          if (!sourceTemplate) {
            notify({
              type: 'error',
              message: 'Template to duplicate was not found'
            });
            return;
          }

          const duplicateName = `${templateName} (copy)`;

          if (sourceTemplate.config.type === 'indicators') {
            openWidget({
              type: 'IndicatorTemplateEditor',
              initialName: duplicateName,
              initialIndicators: templateValueToFormIndicators(
                sourceTemplate.config.value
              )
            });
            return;
          }

          if (sourceTemplate.config.type === 'strategy') {
            openWidget({
              type: 'StrategyTemplateEditor',
              initialName: duplicateName,
              initialStrategy: templateValueToFormStrategy(
                sourceTemplate.config.value
              )
            });
            return;
          }

          if (sourceTemplate.config.type === 'signalBlock') {
            openWidget({
              type: 'SignalBlockTemplateEditor',
              initialName: duplicateName,
              initialConditions: sourceTemplate.config.value.conditions
            });
          }
        } catch (error) {
          notify({
            type: 'error',
            message:
              error instanceof Error
                ? error.message
                : 'Failed to duplicate template'
          });
        }
      })();
    },
    [getTemplateById, getTemplatesByType, notify, openWidget]
  );

  return { onDuplicate };
};
